export const QUEBRA_DE_LINHA = '\n'
export const TAMANHO_MINIMO_DA_FONTE_PT = 6
export const PONTOS_POR_CM = 28.35

const PASSO_DE_REDUCAO_DA_FONTE_PT = 0.5
const FATOR_DE_ALTURA_DA_LINHA = 0.925
const PONTOS_POR_PIXEL = 72 / 96

export interface TextoAjustado {
	linhas: string[]
	tamanhoDaFontePt: number
	alturaDaLinhaPt: number
}

let contexto: CanvasRenderingContext2D | null = null

function contextoDeMedicao(): CanvasRenderingContext2D | null {
	if (contexto) return contexto
	if (typeof document === 'undefined') return null
	contexto = document.createElement('canvas').getContext('2d')
	return contexto
}

export function alturaDaLinhaPt(tamanhoDaFontePt: number): number {
	return tamanhoDaFontePt * FATOR_DE_ALTURA_DA_LINHA
}

function larguraDoTextoPt(texto: string, tamanhoPt: number, negrito: boolean): number {
	const medidor = contextoDeMedicao()
	if (!medidor) return texto.length * tamanhoPt * 0.5
	medidor.font = `${negrito ? 'bold ' : ''}${tamanhoPt}pt Helvetica, Arial, sans-serif`
	return medidor.measureText(texto).width * PONTOS_POR_PIXEL
}

function quebrarEmLinhas(texto: string, tamanhoPt: number, negrito: boolean, larguraMaximaPt: number): string[] {
	const linhas: string[] = []
	for (const paragrafo of texto.split(QUEBRA_DE_LINHA)) {
		linhas.push(...quebrarParagrafo(paragrafo, tamanhoPt, negrito, larguraMaximaPt))
	}
	return linhas.length ? linhas : ['']
}

function quebrarParagrafo(texto: string, tamanhoPt: number, negrito: boolean, larguraMaximaPt: number): string[] {
	const linhas: string[] = []
	for (const palavra of texto.split(' ')) {
		if (!palavra) continue
		const linhaAtual = linhas.length ? linhas[linhas.length - 1] : null
		const candidata = linhaAtual ? `${linhaAtual} ${palavra}` : palavra

		if (larguraDoTextoPt(candidata, tamanhoPt, negrito) <= larguraMaximaPt) {
			if (linhaAtual === null) linhas.push(candidata)
			else linhas[linhas.length - 1] = candidata
			continue
		}
		if (larguraDoTextoPt(palavra, tamanhoPt, negrito) <= larguraMaximaPt) {
			linhas.push(palavra)
		} else {
			linhas.push(...quebrarPalavraGrande(palavra, tamanhoPt, negrito, larguraMaximaPt))
		}
	}
	return linhas.length ? linhas : ['']
}

function quebrarPalavraGrande(palavra: string, tamanhoPt: number, negrito: boolean, larguraMaximaPt: number): string[] {
	const partes: string[] = []
	let atual = ''
	for (const caractere of palavra) {
		if (atual && larguraDoTextoPt(atual + caractere, tamanhoPt, negrito) > larguraMaximaPt) {
			partes.push(atual)
			atual = caractere
		} else {
			atual += caractere
		}
	}
	if (atual) partes.push(atual)
	return partes
}

function limitarAoQueCabe(linhas: string[], alturaDaLinha: number, alturaDisponivel: number): string[] {
	if (alturaDaLinha <= 0) return linhas
	const cabem = Math.floor(alturaDisponivel / alturaDaLinha)
	return cabem >= linhas.length ? linhas : linhas.slice(0, Math.max(0, cabem))
}

export function ajustarTextoNaCaixa(
	texto: string,
	tamanhoInicialPt: number,
	larguraCampoCm: number,
	alturaCampoCm: number,
	negrito: boolean,
): TextoAjustado {
	const larguraCampoPt = larguraCampoCm * PONTOS_POR_CM
	const alturaCampoPt = alturaCampoCm * PONTOS_POR_CM

	if (!texto.includes(QUEBRA_DE_LINHA)) {
		for (let tamanho = tamanhoInicialPt; tamanho >= TAMANHO_MINIMO_DA_FONTE_PT; tamanho -= PASSO_DE_REDUCAO_DA_FONTE_PT) {
			if (larguraDoTextoPt(texto, tamanho, negrito) <= larguraCampoPt && alturaDaLinhaPt(tamanho) <= alturaCampoPt) {
				return { linhas: [texto], tamanhoDaFontePt: tamanho, alturaDaLinhaPt: alturaDaLinhaPt(tamanho) }
			}
		}
	}

	for (let tamanho = tamanhoInicialPt; tamanho >= TAMANHO_MINIMO_DA_FONTE_PT; tamanho -= PASSO_DE_REDUCAO_DA_FONTE_PT) {
		const linhas = quebrarEmLinhas(texto, tamanho, negrito, larguraCampoPt)
		if (linhas.length * alturaDaLinhaPt(tamanho) <= alturaCampoPt) {
			return { linhas, tamanhoDaFontePt: tamanho, alturaDaLinhaPt: alturaDaLinhaPt(tamanho) }
		}
	}

	const altura = alturaDaLinhaPt(TAMANHO_MINIMO_DA_FONTE_PT)
	const linhas = quebrarEmLinhas(texto, TAMANHO_MINIMO_DA_FONTE_PT, negrito, larguraCampoPt)
	return {
		linhas: limitarAoQueCabe(linhas, altura, alturaCampoPt),
		tamanhoDaFontePt: TAMANHO_MINIMO_DA_FONTE_PT,
		alturaDaLinhaPt: altura,
	}
}
